import { sym } from '#library/symbol.library.js';
import { allObject } from '#library/reflection.library.js';
import { deepFreeze } from '#library/utility.library.js';
import { unwrap } from '#library/primitive.library.js';
import { isString, isFunction, isSymbol, isDefined, isNumber, isObject } from '#library/assertion.library.js';
import { registerType } from '#library/type.library.js';
const boundMethodCache = new WeakMap();
/**
 * Creates a configured proxy for an object, optionally returning a revocation function.
 *
 * @param target - The object to wrap
 * @param options - Configuration options for proxy behavior
 * @param isRevocable - Whether to create a revocable proxy
 * @returns The configured proxy, or an object containing the proxy and its revocation function
 * @internal
 */
function factory(target, options = {}, isRevocable = false) {
    const { frozen, lock, appendOnly, onGet, keys, bind, skip } = options;
    const pending = new Set();
    let cachedJSON;
    let result;
    // 1. Unwrap recursive proxies and resolve the true target
    const tgt = unwrap(target);
    // 2. Harden the target if requested
    if (lock)
        deepFreeze(tgt, skip ? { skip } : undefined);
    registerType(tgt);
    const handler = {
        isExtensible: (t) => Reflect.isExtensible(t),
        getPrototypeOf: (t) => Reflect.getPrototypeOf(t),
        setPrototypeOf: (t, proto) => {
            if (frozen)
                throw new TypeError('Security: Prototype mutation attempt on protected object');
            return Reflect.setPrototypeOf(t, proto);
        },
        getOwnPropertyDescriptor: (t, k) => {
            if (keys && !keys.includes(k))
                return undefined;
            if (keys) {
                if (onGet && !isSymbol(k) && !pending.has(k)) {
                    pending.add(k);
                    try {
                        const value = onGet(k, t);
                        if (isDefined(value))
                            return { enumerable: true, configurable: true, value };
                    }
                    finally {
                        pending.delete(k);
                    }
                }
                return { enumerable: true, configurable: true };
            }
            return Reflect.getOwnPropertyDescriptor(t, k);
        },
        ownKeys: (t) => {
            if (keys)
                return keys;
            if (onGet && !pending.has(sym.$Discover)) {
                pending.add(sym.$Discover);
                try {
                    onGet(sym.$Discover, t);
                }
                finally {
                    pending.delete(sym.$Discover);
                }
            }
            return Reflect.ownKeys(t);
        },
        has: (t, k) => (keys ? keys.includes(k) : Reflect.has(t, k)),
        deleteProperty: (t, k) => {
            if (frozen)
                throw new TypeError(`Cannot delete property '${String(k)}' from a protected object.`);
            if (appendOnly && Reflect.has(t, k))
                throw new Error(`Security: Deletion attempt on protected key '${String(k)}'`);
            return Reflect.deleteProperty(t, k);
        },
        defineProperty: (t, k, d) => {
            if (frozen && !Object.isFrozen(t))
                throw new TypeError(`Cannot define property '${String(k)}' on a frozen object.`);
            if (appendOnly && Reflect.has(t, k))
                throw new Error(`Security: Mutation attempt on protected key '${String(k)}'`);
            return Reflect.defineProperty(t, k, d);
        },
        set: (t, k, v, r) => {
            if (frozen && r === result)
                throw new TypeError(`Cannot set property '${String(k)}' on a frozen object.`);
            if (appendOnly) {
                const isTruncating = Array.isArray(t) && k === 'length' && v < t.length;
                if (isTruncating)
                    throw new Error('Security: Truncation attempt on protected array.');
                if (!(Array.isArray(t) && k === 'length') && Reflect.has(t, k))
                    throw new Error(`Security: Mutation attempt on protected key '${String(k)}'`);
            }
            return Reflect.set(t, k, v, r);
        },
        get: (t, k, r) => {
            if (k === sym.$Target)
                return r === result ? t : undefined;
            // Virtualization for serialization
            if (frozen && (k === sym.$Inspect || k === 'toJSON')) {
                const own = Object.getOwnPropertyDescriptor(t, k);
                if (own && isFunction(own.value))
                    return own.value;
                if (!cachedJSON)
                    cachedJSON = () => allObject(t);
                return cachedJSON;
            }
            if (keys && !keys.includes(k))
                return undefined;
            // Property Discovery
            if (onGet && !isSymbol(k) && !Reflect.has(t, k) && !pending.has(k)) {
                pending.add(k);
                try {
                    const val = onGet(k, t);
                    if (isDefined(val))
                        return val;
                }
                finally {
                    pending.delete(k);
                }
            }
            const val = Reflect.get(t, k, r);
            if (bind && k !== 'constructor' && isFunction(val)) {
                const desc = Object.getOwnPropertyDescriptor(t, k);
                if (desc && !desc.configurable && !desc.writable)
                    return val;
                let perTargetCache = boundMethodCache.get(val);
                if (!perTargetCache) {
                    perTargetCache = new WeakMap();
                    boundMethodCache.set(val, perTargetCache);
                }
                const cachedBound = perTargetCache.get(t);
                if (cachedBound)
                    return cachedBound;
                const bound = val.bind(t);
                perTargetCache.set(t, bound);
                return bound;
            }
            return val;
        }
    };
    if (isRevocable) {
        const revocableResult = Proxy.revocable(tgt, handler);
        result = revocableResult.proxy;
        return { proxy: revocableResult.proxy, revoke: revocableResult.revoke };
    }
    result = new Proxy(tgt, handler);
    return result;
}
export function proxify(target, frozen = true, lock = frozen, skip = new WeakSet()) {
    return factory(target, { frozen, lock, skip, bind: frozen });
}
export function delegate(target, onGet, readonly = true) {
    return factory(target, { onGet, frozen: readonly });
}
/**
 * Wraps an object in a protective Proxy that allows extension (adding new keys)
 * but prevents modification or deletion of existing keys.
 *
 * @param target - The object to secure
 * @returns The append-only secured Proxy
 * @example
 * ```ts
 * const ref = secureRef({ initial: 1 });
 * ```
 */
export function secureRef(target) {
    return factory(target, { appendOnly: true });
}
/**
 * Deep-freezes an object and wraps it in a loudly-throwing read-only Proxy.
 * Provides the highest level of noisy immutability.
 *
 * @param obj - The object to secure
 * @param skip - A WeakSet of objects to skip during deep-freeze
 * @returns The securely frozen Proxy
 * @example
 * ```ts
 * const safe = secure({ apiKey: '123' });
 * ```
 */
export function secure(obj, skip = new WeakSet()) {
    return factory(obj, { frozen: true, lock: true, skip, bind: true });
}
/**
 * Creates a virtual Proxy where fixed keys are mapped to a callback function.
 *
 * @param keys - The array of allowed keys (or an object whose ownKeys will be used)
 * @param fn - The callback fired when a key is accessed
 * @returns The virtual delegator object
 * @example
 * ```ts
 * const v = delegator(['a', 'b'], (key) => key.toUpperCase());
 * ```
 */
export function delegator(keys, fn) {
    const keyList = Array.isArray(keys) ? keys : Reflect.ownKeys(keys);
    return factory({}, { keys: keyList, onGet: fn, frozen: true });
}
export function indexedArray(list, finder, readonly = true) {
    return delegate(list, (key) => {
        return (isString(key) && key !== 'length' && !(key in Array.prototype) && !isNumber(Number(key)))
            ? finder(key)
            : undefined;
    }, readonly);
}
/**
 * Creates a proxy that evaluates eligible function-valued properties when read.
 *
 * @param target - The object whose properties are evaluated
 * @returns A proxy that invokes eligible functions with `target` as their receiver; symbols, constructors, and fixed function properties are returned unchanged
 */
export function dynamicProxy(target) {
    if (!isObject(target))
        return target;
    return new Proxy(target, {
        get(t, k, r) {
            if (k === sym.$Target)
                return t;
            const val = Reflect.get(t, k, r);
            if (!isFunction(val) || isSymbol(k) || k === 'constructor')
                return val;
            const desc = Reflect.getOwnPropertyDescriptor(t, k);
            if (desc && !desc.configurable && !desc.writable)
                return val;
            return val.call(t);
        },
        has(t, k) {
            return Reflect.has(t, k);
        },
        ownKeys(t) {
            return Reflect.ownKeys(t);
        },
        getOwnPropertyDescriptor(t, k) {
            return Reflect.getOwnPropertyDescriptor(t, k);
        },
    });
}
/**
 * Creates a proxy that can be permanently revoked on demand.
 *
 * @param target - The object to wrap
 * @param options - Proxy behavior configuration
 * @returns An object containing the proxy and a function that revokes it
 */
export function revocable(target, options = {}) {
    return factory(target, options, true);
}
/**
 * Runs a callback with a temporary proxy that is revoked after execution completes.
 *
 * @param target - The object to expose through the temporary proxy
 * @param fn - The callback that receives the temporary proxy
 * @param options - Configuration options for proxy behavior
 * @returns The callback's result
 */
export function ephemeral(target, fn, options = {}) {
    const { proxy, revoke } = revocable(target, options);
    try {
        const res = fn(proxy);
        if (res && typeof res.then === 'function')
            return Promise.resolve(res).finally(() => revoke());
        revoke();
        return res;
    }
    catch (err) {
        revoke();
        throw err;
    }
}
